import type { AdminColumn, ColumnKind } from "./api";

/** Editing state of one column in the admin row form. */
export interface FieldState {
  text: string;
  isNull: boolean;
  /** Creation only: leave the column out so the application or database fills it. */
  useDefault: boolean;
}

export function toText(kind: ColumnKind, value: unknown): string {
  if (value === null || value === undefined) return "";
  if (kind === "json") return JSON.stringify(value, null, 2);
  return String(value);
}

export function initialFields(
  columns: AdminColumn[],
  row: Record<string, unknown> | null,
): Record<string, FieldState> {
  return Object.fromEntries(
    columns.map((c) => {
      if (row) {
        const value = row[c.name];
        return [c.name, { text: toText(c.kind, value), isNull: value === null, useDefault: false }];
      }
      return [
        c.name,
        {
          text: c.kind === "boolean" ? "false" : "",
          isNull: c.nullable && !c.has_default,
          useDefault: c.has_default,
        },
      ];
    }),
  );
}

export class FieldError extends Error {
  constructor(
    readonly column: string,
    message: string,
  ) {
    super(message);
  }
}

export function parseField(column: AdminColumn, text: string): unknown {
  const fail = (what: string) => {
    throw new FieldError(column.name, `« ${column.name} » : ${what}.`);
  };
  switch (column.kind) {
    case "boolean":
      return text === "true";
    case "integer":
      if (!/^-?\d+$/.test(text.trim())) fail("nombre entier attendu");
      return Number(text.trim());
    case "number": {
      const value = Number(text.trim().replace(",", "."));
      if (text.trim() === "" || Number.isNaN(value)) fail("nombre attendu");
      return value;
    }
    case "json":
      try {
        return JSON.parse(text);
      } catch {
        return fail("JSON invalide");
      }
    default:
      return text;
  }
}

/**
 * Values to send. On creation, every column not left to its default; on edit,
 * only the columns whose value changed.
 */
export function buildValues(
  columns: AdminColumn[],
  fields: Record<string, FieldState>,
  original: Record<string, unknown> | null,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const column of columns) {
    const field = fields[column.name];
    if (!field || (!original && field.useDefault)) continue;
    const value = field.isNull ? null : parseField(column, field.text);
    if (original && JSON.stringify(value) === JSON.stringify(original[column.name] ?? null)) continue;
    values[column.name] = value;
  }
  return values;
}
